import { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { IonAvatar, IonContent, IonHeader, IonIcon, IonImg, IonMenu, IonPage, IonToolbar } from '@ionic/react';
import {
    calendarOutline,
    checkmarkCircleOutline,
    clipboardOutline,
    createOutline,
    listOutline,
    personOutline,
    settingsOutline,
    timeOutline,
} from 'ionicons/icons';
import { useAtomValue } from 'jotai';

import madper from '../../assets/images/madper.png';
import { useAppConfig, useUser } from '../../hooks/useSwr';
import { usersWithTypeTextAtom } from '../../utils/Global';

export const SplitPane: React.FC<{ children: React.ReactNode; bottomPanel?: React.ReactNode }> = ({
    children,
    bottomPanel,
}) => {
    const location = useLocation();
    const { data: appConfig } = useAppConfig();
    const { mainUser } = useAtomValue(usersWithTypeTextAtom);
    const { user } = useUser();
    const menuRef = useRef<HTMLIonMenuElement>(null);

    const isModePointage = appConfig?.appMode === 'live';
    const isModeManual = appConfig?.appMode === 'manual';
    const isAdherent = mainUser?.type === 'adherent';
    const isGeiq = mainUser?.type === 'geiq';
    const isEmployee = mainUser?.type === 'employee';

    const getClassDependsPath = (path: string) => {
        const isActive = location.pathname === path;
        return `menu-item group relative flex items-center px-4 py-3 mx-2 my-1 rounded-lg transition-all duration-300 ease-in-out cursor-pointer ${
            isActive
                ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/30'
                : 'text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-600 hover:shadow-md'
        }`;
    };

    const MenuSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
        <div className="mb-6">
            <div className="px-4 mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</h3>
            </div>
            <div className="space-y-1">{children}</div>
        </div>
    );

    const MenuItem: React.FC<{
        href: string;
        icon: string;
        label: string;
    }> = ({ href, icon, label }) => {
        const handleClick = () => {
            if (menuRef.current) {
                menuRef.current.close();
            }
        };

        return (
            <Link to={href} className="no-underline" onClick={handleClick}>
                <div className={getClassDependsPath(href)}>
                    <IonIcon icon={icon} className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span className="flex-1 font-medium">{label}</span>
                    <div className="absolute inset-0 rounded-lg ring-2 ring-transparent group-hover:ring-orange-200 transition-all duration-300"></div>
                </div>
            </Link>
        );
    };

    return (
        <>
            <IonMenu ref={menuRef} contentId="main" menuId="side-menu" className="modern-menu">
                <IonHeader className="shadow-none">
                    <IonToolbar className="bg-gradient-to-r from-orange-500 via-orange-600 to-red-500">
                        <div className="flex items-center justify-center py-4">
                            <IonImg
                                className="max-w-none h-16 w-auto object-contain filter brightness-110"
                                src={madper}
                                alt="Madper Logo"
                            />
                        </div>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="bg-gray-50">
                    <div className="p-4 space-y-2">
                        {/* User Info Section */}
                        {mainUser && (
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
                                <div className="flex items-center space-x-3">
                                    <IonAvatar className="w-10 h-10">
                                        <div className="w-full h-full bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                                            <IonIcon icon={personOutline} className="text-white text-lg" />
                                        </div>
                                    </IonAvatar>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 truncate">{user?.name}</p>
                                        <p className="text-xs text-gray-500">{mainUser.typeText}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Validation Section - for Adherents */}
                        {isAdherent && (
                            <MenuSection title="Validation">
                                <MenuItem
                                    href="/validation"
                                    icon={checkmarkCircleOutline}
                                    label="Validation pointage"
                                />
                                <MenuItem href="/validationconges" icon={calendarOutline} label="Validation congés" />
                            </MenuSection>
                        )}

                        {/* Employee Section */}
                        {isEmployee && (
                            <MenuSection title="Personnel">
                                {isModePointage && (
                                    <MenuItem href="/pointage" icon={timeOutline} label="Pointage" />
                                )}
                                {isModeManual && (
                                    <MenuItem href="/saisie" icon={createOutline} label="Saisie" />
                                )}
                                <MenuItem href="/mesconges" icon={calendarOutline} label="Congés" />
                                {isModePointage && (
                                    <MenuItem href="/mespointage" icon={listOutline} label="Mes pointages" />
                                )}
                            </MenuSection>
                        )}

                        {/* Management Section */}
                        {isGeiq && (
                            <MenuSection title="Gestion">
                                <MenuItem href="/schedule" icon={clipboardOutline} label="Planning" />
                            </MenuSection>
                        )}

                        {/* Settings Section */}
                        <MenuSection title="Configuration">
                            <MenuItem href="/settings" icon={settingsOutline} label="Paramètres" />
                        </MenuSection>
                    </div>

                    {/* Bottom Panel */}
                    {bottomPanel && <div className="mt-auto p-4 border-t border-gray-200 bg-white">{bottomPanel}</div>}
                </IonContent>
            </IonMenu>
            <IonPage id="main">{children}</IonPage>
        </>
    );
};
